import React, { createContext, ReactNode, useContext, useMemo } from 'react';

export type AccessTokenProvider = () => Promise<string | null> | string | null;
export type RefreshAccessTokenProvider = () => Promise<string | null> | string | null;

export interface UltraMeetClientConfig {
    /** Base URL of the Ultra-Meet frontend/backend API, e.g. https://meet.example.com. */
    apiBaseUrl?: string;
    /** Base URL where static assets are served, e.g. https://meet.example.com or a CDN URL. */
    assetBaseUrl?: string;
    /** Optional fetch implementation for non-browser environments or custom instrumentation. */
    fetchImpl?: typeof fetch;
    /** Optional host-app access token provider used by authenticatedFetch. */
    accessTokenProvider?: AccessTokenProvider;
    /** Optional host-app token refresh provider used after a 401 response. */
    refreshAccessTokenProvider?: RefreshAccessTokenProvider;
}

const DEFAULT_CONFIG: Required<Pick<UltraMeetClientConfig, 'apiBaseUrl' | 'assetBaseUrl'>> = {
    apiBaseUrl: '',
    assetBaseUrl: ''
};

let currentConfig: UltraMeetClientConfig = { ...DEFAULT_CONFIG };

const UltraMeetConfigContext = createContext<UltraMeetClientConfig>(currentConfig);

function normalizeBaseUrl(baseUrl?: string): string {
    return (baseUrl || '').replace(/\/+$/, '');
}

function isAbsoluteUrl(url: string): boolean {
    return /^(?:[a-z][a-z\d+.-]*:)?\/\//i.test(url) || url.startsWith('data:') || url.startsWith('blob:');
}

function joinUrl(baseUrl: string | undefined, path: string): string {
    if (!path || isAbsoluteUrl(path)) {
        return path;
    }

    const base = normalizeBaseUrl(baseUrl);
    if (!base) {
        return path;
    }

    if (path.startsWith('/')) {
        return `${base}${path}`;
    }

    return `${base}/${path}`;
}

export function configureUltraMeetClient(config: UltraMeetClientConfig): void {
    currentConfig = {
        ...currentConfig,
        ...config,
        apiBaseUrl: config.apiBaseUrl !== undefined ? normalizeBaseUrl(config.apiBaseUrl) : currentConfig.apiBaseUrl,
        assetBaseUrl: config.assetBaseUrl !== undefined ? normalizeBaseUrl(config.assetBaseUrl) : currentConfig.assetBaseUrl
    };
}

export function getUltraMeetClientConfig(): UltraMeetClientConfig {
    return currentConfig;
}

export function resolveApiUrl(url: string | URL): string {
    const value = url instanceof URL ? url.toString() : url;
    return joinUrl(currentConfig.apiBaseUrl, value);
}

export function resolveAssetUrl(url: string | null | undefined): string {
    if (!url) {
        return '';
    }
    return joinUrl(currentConfig.assetBaseUrl, url);
}

export async function apiFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    const fetchImpl = currentConfig.fetchImpl || fetch;

    if (typeof input === 'string' || input instanceof URL) {
        return fetchImpl(resolveApiUrl(input), init);
    }

    if (typeof Request !== 'undefined' && input instanceof Request) {
        return fetchImpl(new Request(resolveApiUrl(input.url), input), init);
    }

    return fetchImpl(input, init);
}

export interface UltraMeetConfigProviderProps extends UltraMeetClientConfig {
    children: ReactNode;
}

export const UltraMeetConfigProvider: React.FC<UltraMeetConfigProviderProps> = ({ children, ...config }) => {
    const value = useMemo<UltraMeetClientConfig>(
        () => ({
            ...currentConfig,
            ...config,
            apiBaseUrl: config.apiBaseUrl !== undefined ? normalizeBaseUrl(config.apiBaseUrl) : currentConfig.apiBaseUrl,
            assetBaseUrl: config.assetBaseUrl !== undefined ? normalizeBaseUrl(config.assetBaseUrl) : currentConfig.assetBaseUrl
        }),
        [config.apiBaseUrl, config.assetBaseUrl, config.fetchImpl, config.accessTokenProvider, config.refreshAccessTokenProvider]
    );

    configureUltraMeetClient(value);

    return <UltraMeetConfigContext.Provider value={value}>{children}</UltraMeetConfigContext.Provider>;
};

export function useUltraMeetConfig(): UltraMeetClientConfig {
    return useContext(UltraMeetConfigContext);
}
